import React, { useEffect, useRef, useState } from 'react';
import type { FormField, FormPage, ThemeConfig } from '../../types/forms';
import { getAlignmentClass } from '../../lib/getAlignmentClass';

interface MultiQuestionSlideProps {
  fields: FormField[];
  page: FormPage;
  themeConfig: ThemeConfig;
  errors: Record<string, string>;
  renderField: (field: FormField) => React.ReactNode;
}

// Static Tailwind classes — dynamic interpolation breaks purging
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
};

const FULL_SPAN: Record<number, string> = {
  1: '',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
};

// field.width → col-span per grid size
const SPAN_MAP: Record<number, Record<string, string>> = {
  1: { full: '', half: '', third: '' },
  2: { full: 'sm:col-span-2', half: '', third: '' },
  3: { full: 'sm:col-span-3', half: 'sm:col-span-2', third: '' },
};

export const MultiQuestionSlide: React.FC<MultiQuestionSlideProps> = ({
  fields,
  page,
  themeConfig,
  errors,
  renderField,
}) => {
  const cols = page.columns || 1;
  const isMultiCol = cols > 1;

  // saas-1.24.A.1 hotfix Bug 4: mask gradient só aparece quando há overflow real (não é crop seco)
  // mobile-ui.3.C AC4: recomputa em resize/orientationchange (mesmo cálculo; rAF debounce)
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  useEffect(() => {
    const check = () => {
      if (!scrollerRef.current) return;
      setHasOverflow(scrollerRef.current.scrollHeight > scrollerRef.current.clientHeight + 1);
    };
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(check);
    };
    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
    };
  }, [page.id, fields.length]);

  const getSpan = (field: FormField): string => {
    // File fields always full width (UX rule)
    if (field.type === 'file') return FULL_SPAN[cols] || '';
    const width = field.width || 'full';
    return SPAN_MAP[cols]?.[width] || '';
  };

  return (
    <>
      {/* Page title FIXO (saas-1.23.C AC9: fora do scroller) */}
      {page.title && (
        <h2
          className="text-2xl md:text-3xl short:text-lg font-bold mb-2"
          style={{ color: themeConfig.textColor }}
        >
          {page.title}
        </h2>
      )}
      {/* Page description FIXO (saas-1.23.C AC9: fora do scroller; opcional, omitida quando undefined) */}
      {page.description && (
        <p
          className="text-base short:text-sm mb-6 short:mb-3"
          style={{ color: themeConfig.textColor, opacity: 0.6 }}
        >
          {page.description}
        </p>
      )}
      {/* Scroller — apenas a grade de fields (mobile only; desktop herda main scroll).
          saas-1.24.A.1 hotfix Bug 3+4: max-h reduzida + fade mask 80px aplicado APENAS quando há overflow real */}
      <div
        ref={scrollerRef}
        className="max-h-[min(440px,55vh)] overflow-y-auto md:max-h-none md:overflow-y-visible md:mask-none"
        style={hasOverflow ? {
          maskImage: 'linear-gradient(to bottom, black calc(100% - 80px), transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 80px), transparent 100%)',
        } : undefined}
      >
        <div className={`grid ${GRID_COLS[cols] || 'grid-cols-1'} gap-6 short:gap-3`}>
          {fields.map((field) => {
        // Context fields (heading/paragraph) rendered inline, full width
        if (field.type === 'heading') {
          return (
            <div key={field.id} className={FULL_SPAN[cols] || ''}>
              <h2
                className={`${isMultiCol ? 'text-lg' : 'text-xl md:text-2xl'} font-semibold`}
                style={{ color: themeConfig.textColor, opacity: 0.8 }}
              >
                {field.label}
              </h2>
            </div>
          );
        }

        if (field.type === 'paragraph') {
          return (
            <div key={field.id} className={FULL_SPAN[cols] || ''}>
              <p className="text-base" style={{ color: themeConfig.textColor, opacity: 0.6 }}>
                {field.placeholder || field.label}
              </p>
            </div>
          );
        }

        const span = getSpan(field);
        const error = errors[field.id];

        return (
          <div key={field.id} className={`${span} min-w-0 ${getAlignmentClass(field.alignment)}`} data-field-id={field.id}>
            {/* Label — smaller in multi-column */}
            {field.type !== 'statement' && (
              <label
                className={`block ${isMultiCol ? 'text-lg font-semibold' : 'text-2xl md:text-3xl font-bold'} short:text-lg mb-2`}
                style={{ color: themeConfig.textColor }}
              >
                {field.label}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </label>
            )}

            {/* Help text */}
            {field.helpText && (
              <p
                className={`${isMultiCol ? 'text-sm' : 'text-base'} short:text-sm mb-4 short:mb-2`}
                style={{ color: themeConfig.textColor, opacity: 0.6 }}
              >
                {field.helpText}
              </p>
            )}

            {/* Field input */}
            <div className="mt-2">{renderField(field)}</div>

            {/* Error */}
            {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          </div>
        );
      })}
        </div>
      </div>
    </>
  );
};
